/**
 * AMPD DCMS2 — Action Dock
 *
 * Floating quick-action dock docked to the left viewport edge, successor of
 * the DCMS v1 scroll menu (scroll-to-top + open-menu buttons). It lives in
 * the HOST document only: position:fixed in the top window is natively
 * smooth, unlike JS-emulated sticky inside the stretched dataset iframe.
 *
 * Sections (separated by a thin rule):
 *  - navigation: scroll to top, open the main menu (parity with DCMS v1)
 *  - record actions: a "return without saving" mirror (arrow_back, cloned from
 *    the edit/insert/view form's return button) followed by a single save
 *    trigger that opens a flyout (to the right) with the save modes — an AJAX
 *    "save without reload" plus mirrors of the real submit buttons found in the
 *    dataset iframe edit form (icon, color and label are cloned from the real
 *    buttons; activating any mirror proxies the click to the original so form
 *    logic — the return button's own unsaved-changes confirm, editor
 *    pre-flush, validation, dirty tracking, progress UI — stays untouched)
 *  - custom groups registered through the public API (see below)
 *  - meta: proxy for the feedback gadget launcher, when the gadget is present
 *
 * Public API (host window; reachable from same-origin iframes via
 * window.parent.DCMSActionDock):
 *
 *   DCMSActionDock.register(groupId, buttons)
 *     buttons: [{ icon, label, onClick, colorClass?, flyout?: [items],
 *                 flyoutRender?: fn(panel) }]
 *     A button with `flyout` opens a right-side submenu of items
 *     ({ icon, label, onClick, colorClass? }) instead of firing onClick.
 *     A button with `flyoutRender` opens the same submenu as an empty PANEL
 *     and hands the element to the callback on every open, so a page can put
 *     arbitrary content there (a table, a form) and refresh it each time.
 *     The dock keeps owning the chrome: open/close, click-outside, Escape,
 *     per-edge placement, dark mode and the viewport clamp. `onClick` is
 *     ignored when either flyout form is given.
 *   DCMSActionDock.unregister(groupId)
 *
 *   DCMSActionDock.saveAjax() — run the dock's background save from the framed
 *     page (a form may want its own "save and stay" button on the same
 *     pipeline). No-op when the page has no save button.
 *
 *   DCMSActionDock.supports — capability map ({ flyoutRender, saveAjax }) so
 *     callers can feature-detect a newer dock and fall back gracefully on an
 *     older one (a bundle lagging behind the page code).
 *
 *   DCMSActionDock.setAlwaysVisible(bool) — reveal the dock from page load
 *     (no scroll threshold); cleared on dataset iframe navigation.
 *
 *   Custom groups are cleared automatically whenever the dataset iframe
 *   navigates to a new document — pages must (re)register on their own load.
 *
 * AJAX save ("save without reload") rides the ENTIRE native save pipeline:
 * it replays the user gesture (pointerdown → click) on the real submit
 * button, so every page trigger runs untouched — progress UI and its
 * capability token, editor pre-flush and validation, media/meta gates,
 * dirty bookkeeping. Only the FINAL form submission is hijacked (one-shot
 * capture listener registered last) and replaced with a fetch POST of the
 * exact same payload. If any gate stops the save, no submit fires and the
 * hijack disarms on a timer. After the POST the dock dispatches on the
 * iframe document: `dcms:dock:saved` (success — page code re-baselines
 * dirty tracking, progress UI fetches its final report) or
 * `dcms:dock:save-rejected` (server-side validation / network failure).
 *
 * In EDIT the payload always carries submitAction=saveAndContinue — the only
 * response that comes back as the re-rendered FORM, which is what the resync
 * below needs — and a rejected background save is REPLAYED as a normal submit,
 * so the user lands on the page stating the reason instead of being left with
 * a toast and unsaved work. The write-conflict branch is exempt (there not
 * saving is the user's own answer), and so is INSERT (a replay could create a
 * second record).
 *
 * A successful save answers with the re-rendered form, so before announcing
 * it the dock copies the server-owned hidden state back into the live DOM
 * (see RESYNC_HIDDEN) — without it the page would keep pre-save values the
 * save has just invalidated. The `dcms:dock:saved` detail carries the
 * response (`html`, parsed `document`, `resynced` map) so page code can pull
 * whatever else a reload would have refreshed.
 *
 * Record-action state mirroring is visual: a real button is considered
 * inactive when it is disabled or when the page dims it (computed opacity),
 * which covers project-specific "no unsaved changes" stylings without any
 * coupling to their code.
 *
 * The dock appears only after the page is scrolled past the threshold, so it
 * never covers anything while the natural header/footer actions are in view.
 * A hide control (×) retracts it to a restore HANDLE at the same edge, for
 * pages where even the floating dock is in the way (e.g. a left-hand search
 * column). The handle wears the dock's own chrome in miniature (background,
 * border, rounded inner corner, shadow) plus a chevron pointing the way the
 * dock slides back in, so it stays findable on a dense, dark or image-heavy
 * page — the bare dot it replaces was routinely missed.
 *
 * Hiding is deliberately NOT persisted: it means "get out of my way HERE",
 * never "switch the dock off for good". The dock returns on a reload and as
 * soon as the dataset iframe navigates to another record, so a stray × can
 * never leave an editor without the bar for the rest of the day.
 *
 * A move control (four-arrow icon next to the hide ×) cycles the dock through
 * the edges (left → top → right → bottom). The picked edge persists in
 * localStorage as an override on top of the dockPosition preference (matching
 * the settings form's precedence: browser localStorage > database > defaults)
 * and is cleared whenever the appearance-preferences form saves — an explicit
 * save always wins over a quick reposition.
 *
 * Appearance preferences (user_settings → dcms2.interface, injected into the
 * host page as window.__DCMS_INTERFACE_SETTINGS__ and re-applied live via the
 * 'dcms-apply-settings' postMessage sent by the appearance-preferences form):
 *
 *   dockPosition: 'left' (default) | 'right' | 'both' | 'top' | 'bottom'
 *     'both' keeps the dock on ONE active vertical edge and shows a discreet
 *     dot on the opposite edge — clicking that dot moves the dock over (the
 *     previous edge gets the dot). The active edge persists in localStorage.
 *     'top'/'bottom' pin the dock as a centered HORIZONTAL bar at that edge:
 *     same groups laid out in a row, separators rotated to vertical rules and
 *     flyouts opening towards the screen center. The row layout leaves space
 *     for optional button labels (a planned wide-button mode).
 *   dockDot: 'scroll' (default) | 'always'
 *     'scroll' shows the restore dot only after the page is scrolled past the
 *     dock threshold (historic behaviour); 'always' keeps the dot permanently
 *     on screen — clicking it before any scroll force-reveals the dock until
 *     the next scroll returns above the threshold.
 *
 * Phone form mode (viewport up to 640 px, the dataset iframe showing an
 * edit/insert form): the dock is the sticky save bar of the form — pinned to
 * the bottom edge as a horizontal bar (a manual edge pick and the top bar
 * preference win), expanded unless the user collapsed it, on screen without
 * scrolling. The host page gets html.dcms-dock-phone-form (bottom padding).
 * A step-by-step form (Tabs flow 'steps') registers its Back / Next buttons
 * as a custom group, so they ride in the same bar.
 *
 * Opt-out: set window.__DCMS_NO_ACTION_DOCK = true before DOMContentLoaded.
 * Labels can be overridden via window.__DCMS_ACTION_DOCK_LABELS__ = {...}.
 *
 * @copyright AMPD Sp. z o.o. / PTWP-ONLINE Sp. z o.o.
 * @author    Dominik Szmidt
 * @link      https://www.ampd.pl
 * @license   Commercial - usage requires a valid license
 * @see       /doc Documentation directory
 * @see       https://docs.ampd.pl Online documentation and examples
 */
(function () {
    'use strict';

    var SHOW_AFTER_PX = 5;
    var SYNC_INTERVAL_MS = 1200;
    // The GremiEditor fast path primes its pre-flush on pointerdown and expects
    // the click ~150 ms later (measured); a proxied submit reproduces that gap.
    var PROXY_CLICK_DELAY_MS = 250;

    var L = window.__DCMS_ACTION_DOCK_LABELS__ || {};
    function t(key, fallback) { return (L[key] != null) ? L[key] : fallback; }

    // Below this viewport width the dock would eat a big share of the row: it
    // starts collapsed to a thin handle and the save flyout opens upward
    // (see injectStyles) instead of to the right, where it would overflow.
    var NARROW_MQ_QUERY = '(max-width: 640px)';
    var COLLAPSE_KEY = 'dcms.actionDock.collapsed';
    // Full hide (any width): the dock retracts to a restore handle at the same
    // edge. Distinct from the narrow-screen collapse, which only folds the dock
    // down to its handle. Intentionally session-scoped — held in memory only,
    // so a reload or a move to another record brings the dock back (see the
    // header). The key is legacy: earlier builds persisted the choice and the
    // stored '1' is cleared on boot so nobody stays without the bar.
    var HIDE_KEY = 'dcms.actionDock.hidden';
    // Active edge in the 'both' position mode ('left'|'right') — which edge
    // currently carries the dock; the opposite one shows the switch dot.
    var SIDE_KEY = 'dcms.actionDock.side';
    // Manual edge override picked with the move control ('left'|'right'|'top'|
    // 'bottom'). Sits ABOVE the dockPosition preference (localStorage beats the
    // database, as the settings form documents) and is cleared when the
    // appearance-preferences form saves.
    var EDGE_KEY = 'dcms.actionDock.edge';
    var EDGE_ORDER = ['left', 'top', 'right', 'bottom'];
    // Restore-handle chevrons, per edge the handle sits on.
    var DOT_CHEVRONS = {
        left: 'chevron_right',
        right: 'chevron_left',
        top: 'expand_more',
        bottom: 'expand_less'
    };

    var dock = null, navGroup = null, recordGroup = null, customWrap = null, metaGroup = null;
    var collapseBtn = null, collapsed = false;
    var hideBtn = null, moveBtn = null, dotLeft = null, dotRight = null, dotTop = null, dotBottom = null, hidden = false;
    var narrowMq = window.matchMedia ? window.matchMedia(NARROW_MQ_QUERY) : null;
    var visible = false, syncTimer = null, recordSignature = '', trackedDoc = null;
    var customGroups = {};
    var recordFlyout = null;   // {trigger, items:[{btn, real|null}]}
    // Appearance preferences (see the header comment): dock edge(s), active
    // edge and restore-dot mode. revealOverride force-shows the dock after a
    // dot click while the page is not scrolled (only reachable with
    // dockDot='always'); any scroll back above the threshold clears it.
    var posPref = 'left', side = 'left', dotAlways = false, revealOverride = false;
    // Page-requested always-on mode (DCMSActionDock.setAlwaysVisible): the dock
    // ignores the scroll threshold and stays revealed from page load. Opt-in per
    // dataset document — reset whenever the dataset iframe navigates (same
    // lifecycle as custom groups), so pages must re-request it on their own
    // load and every other page keeps the default scroll-reveal behavior.
    var alwaysVisible = false;
    // Phone form mode: under the narrow breakpoint, while the dataset iframe
    // shows an edit/insert form (form[name="datasetEditForm"]), the dock is
    // the form's sticky save bar — a horizontal bar at the bottom edge (unless
    // the user moved the dock to an edge by hand or prefers the top bar),
    // expanded (unless the user collapsed it) and on screen without scrolling.
    // Not persisted: leaving the form (or widening the window) restores the
    // regular edge, collapse default and scroll reveal.
    var phoneForm = false, phoneFormTimer = null, lastPrefs = {};

    function boot() {
        if (window !== window.top) { return; }
        if (window.__DCMS_NO_ACTION_DOCK) { return; }
        if (window.__dcmsActionDockBooted) { return; }
        if (!document.querySelector('header.topbar')) { return; }
        window.__dcmsActionDockBooted = true;

        injectStyles();
        build();
        initCollapsed();
        initHidden();
        applyInterfacePrefs(readInterfaceSettings());
        bindScroll();
        bindFlyoutClose();
        bindSettingsMessages();
        exposeApi();
        initPhoneForm();

        // The feedback gadget mounts its launcher on DOMContentLoaded, which may
        // fire after this module evaluates — re-sync once the page settles.
        syncFeedbackAction();
        document.addEventListener('DOMContentLoaded', syncFeedbackAction);
        window.addEventListener('load', syncFeedbackAction);
    }

    /* ---- DOM ------------------------------------------------------------ */

    function injectStyles() {
        var css = ''
            + '.dcms-action-dock{position:fixed;left:0;bottom:88px;z-index:39;display:flex;flex-direction:column;gap:5px;'
            + 'padding:7px 6px;background:#fff;border:1px solid var(--color-gray-200);border-left:0;'
            + 'border-radius:0 10px 10px 0;box-shadow:0 4px 16px rgba(0,0,0,.14);'
            + 'transform:translateX(-110%);transition:transform .25s ease;}'
            + 'html.dark .dcms-action-dock{background:var(--color-neutral-800);border-color:var(--color-neutral-600)}'
            + '.dcms-action-dock.is-visible{transform:none}'
            + '.dcms-action-dock .dcms-dock-group{display:flex;flex-direction:column;gap:5px}'
            + '.dcms-action-dock .dcms-dock-group:empty{display:none}'
            + '.dcms-action-dock .dcms-dock-group + .dcms-dock-group:not(:empty){border-top:1px solid var(--color-gray-200);padding-top:5px}'
            + 'html.dark .dcms-action-dock .dcms-dock-group + .dcms-dock-group:not(:empty){border-top-color:var(--color-neutral-600)}'
            + '.dcms-action-dock .dcms-dock-inactive{opacity:.5;filter:saturate(.55)}'
            + '.dcms-dock-item{position:relative}'
            + '.dcms-dock-flyout{position:absolute;left:calc(100% + 12px);bottom:0;display:none;flex-direction:column;gap:4px;'
            + 'padding:6px;background:#fff;border:1px solid var(--color-gray-200);border-radius:10px;'
            + 'box-shadow:0 6px 20px rgba(0,0,0,.16);white-space:nowrap;z-index:1}'
            + 'html.dark .dcms-dock-flyout{background:var(--color-neutral-800);border-color:var(--color-neutral-600)}'
            + '.dcms-dock-item.is-open .dcms-dock-flyout{display:flex}'
            + '.dcms-dock-flyout .btn{justify-content:flex-start}'
            // Custom-content flyout (flyoutRender): a page-owned panel instead
            // of a button column — it needs room, wrapping text and its own
            // scrollbar, none of which the submenu metrics provide.
            + '.dcms-dock-flyout.is-panel{padding:10px 12px;white-space:normal;'
            + 'min-width:340px;max-width:min(620px,calc(100vw - 24px));'
            + 'max-height:min(60vh,520px);overflow:auto}'
            + '.dcms-dock-busy{animation:dcmsDockPulse 1s ease-in-out infinite}'
            + '@keyframes dcmsDockPulse{50%{opacity:.4}}'
            // Collapse handle: hidden on wide screens (dock always expanded there),
            // shown only under the narrow breakpoint where the dock defaults to
            // collapsed and the user taps to reveal the actions.
            + '.dcms-dock-collapse{display:none;align-items:center;justify-content:center;'
            + 'padding:1px 0;margin:-2px 0 1px;border:0;background:transparent;cursor:pointer;'
            + 'color:var(--color-gray-500)}'
            + 'html.dark .dcms-dock-collapse{color:var(--color-neutral-300)}'
            + '.dcms-dock-collapse .msr{font-size:18px}'
            // System commands (hide ×, move ⤢, scroll-to-top, open-menu): plain
            // borderless icons, visually distinct from the boxed action
            // buttons. Saturated enough to read at a glance, still quieter
            // than the record actions.
            + '.dcms-dock-sys{display:flex;align-items:center;justify-content:center;'
            + 'padding:1px 0;border:0;background:transparent;cursor:pointer;'
            + 'color:var(--color-gray-600)}'
            + 'html.dark .dcms-dock-sys{color:var(--color-neutral-200)}'
            + '.dcms-dock-sys:hover{color:var(--color-gray-900)}'
            + 'html.dark .dcms-dock-sys:hover{color:var(--color-neutral-50)}'
            + '.dcms-dock-sys .msr{font-size:18px}'
            // Hide/move keep their compact metrics on top of the shared look.
            + '.dcms-dock-hide{padding:0;margin:-3px 0 1px}'
            + '.dcms-dock-hide .msr{font-size:15px}'
            + '.dcms-dock-move{padding:0;margin:-2px 0 1px}'
            + '.dcms-dock-move .msr{font-size:14px}'
            // Restore dot: a tiny discreet target at the dock's corner, shown
            // only while the dock is hidden AND the page is scrolled (matching
            // the dock's own reveal). Click brings the dock back.
            + '.dcms-dock-dot{position:fixed;left:0;bottom:88px;z-index:39;display:none;'
            + 'align-items:center;justify-content:center;width:22px;height:26px;padding:0;'
            + 'border:0;background:transparent;cursor:pointer}'
            + '.dcms-dock-dot.is-visible{display:flex}'
            + '.dcms-dock-dot::before{content:"";width:9px;height:9px;border-radius:50%;'
            + 'background:var(--color-gray-400);opacity:.45;transition:opacity .2s,transform .2s}'
            + '.dcms-dock-dot:hover::before{opacity:.95;transform:scale(1.3)}'
            + 'html.dark .dcms-dock-dot::before{background:var(--color-neutral-400)}'
            // Restore handle (`.is-restore`, i.e. the hidden-dock state): the
            // dock's own chrome shrunk to a tab on the edge, with a chevron
            // pointing the way the dock slides back in. The bare dot survives
            // for the OTHER role this element plays — the 'both' mode's switch
            // target, which sits next to a dock that is already on screen and
            // must stay quiet. A hidden dock has no such anchor, so its handle
            // carries background, border and shadow: it has to read on a dense
            // page and on dark or image-heavy skins alike.
            + '.dcms-dock-dot.is-restore{width:22px;height:46px;background:#fff;'
            + 'border:1px solid var(--color-gray-200);border-left:0;border-radius:0 9px 9px 0;'
            + 'box-shadow:0 3px 12px rgba(0,0,0,.16);color:var(--color-gray-600);'
            + 'transition:color .2s,background-color .2s}'
            + 'html.dark .dcms-dock-dot.is-restore{background:var(--color-neutral-700);'
            + 'border-color:var(--color-neutral-500);color:var(--color-neutral-50)}'
            + '.dcms-dock-dot.is-restore::before{display:none}'
            + '.dcms-dock-dot .msr{display:none}'
            + '.dcms-dock-dot.is-restore .msr{display:inline-flex;font-size:18px}'
            + '.dcms-dock-dot.is-restore:hover{color:var(--color-gray-900);'
            + 'background:var(--color-gray-50)}'
            + 'html.dark .dcms-dock-dot.is-restore:hover{color:#fff;'
            + 'background:var(--color-neutral-600)}'
            // Per-edge chrome: the border and the rounded corners always face
            // the screen, the flat side sits flush with the viewport edge.
            + '.dcms-dock-dot.is-restore.dcms-dock-right{border-left:1px solid var(--color-gray-200);'
            + 'border-right:0;border-radius:9px 0 0 9px}'
            + '.dcms-dock-dot.is-restore.dcms-dock-top,'
            + '.dcms-dock-dot.is-restore.dcms-dock-bottom{width:46px;height:22px;'
            + 'border-left:1px solid var(--color-gray-200)}'
            + '.dcms-dock-dot.is-restore.dcms-dock-top{border-top:0;border-radius:0 0 9px 9px}'
            + '.dcms-dock-dot.is-restore.dcms-dock-bottom{border-bottom:0;border-radius:9px 9px 0 0}'
            + 'html.dark .dcms-dock-dot.is-restore.dcms-dock-right,'
            + 'html.dark .dcms-dock-dot.is-restore.dcms-dock-top,'
            + 'html.dark .dcms-dock-dot.is-restore.dcms-dock-bottom{'
            + 'border-left-color:var(--color-neutral-500)}'
            // Right-edge variant (dockPosition 'right', or 'both' with the right
            // edge active): mirrored chrome and the flyout opening to the left.
            + '.dcms-action-dock.dcms-dock-right{left:auto;right:0;border-left:1px solid var(--color-gray-200);'
            + 'border-right:0;border-radius:10px 0 0 10px;transform:translateX(110%)}'
            + 'html.dark .dcms-action-dock.dcms-dock-right{border-left-color:var(--color-neutral-600)}'
            + '.dcms-action-dock.dcms-dock-right.is-visible{transform:none}'
            + '.dcms-action-dock.dcms-dock-right .dcms-dock-flyout{left:auto;right:calc(100% + 12px)}'
            + '.dcms-dock-dot.dcms-dock-right{left:auto;right:0}'
            // Horizontal variants (dockPosition 'top'/'bottom'): the dock turns
            // into a centered horizontal bar pinned to that edge. Same groups,
            // laid out in a row; separators become vertical rules and flyouts
            // open towards the screen center (down from the top bar, up from
            // the bottom one). flex-wrap keeps an overlong bar readable instead
            // of clipping it; the shared centering transform is kept in every
            // state, so reveal animates only the translateY component.
            + '.dcms-action-dock.dcms-dock-top,.dcms-action-dock.dcms-dock-bottom{'
            + 'flex-direction:row;align-items:center;flex-wrap:wrap;justify-content:center;'
            + 'left:50%;right:auto;max-width:calc(100vw - 16px);'
            + 'border-left:1px solid var(--color-gray-200)}'
            + 'html.dark .dcms-action-dock.dcms-dock-top,'
            + 'html.dark .dcms-action-dock.dcms-dock-bottom{border-left-color:var(--color-neutral-600)}'
            + '.dcms-action-dock.dcms-dock-top{top:0;bottom:auto;border-top:0;'
            + 'border-radius:0 0 10px 10px;transform:translate(-50%,-110%)}'
            + '.dcms-action-dock.dcms-dock-bottom{bottom:0;top:auto;border-bottom:0;'
            + 'border-radius:10px 10px 0 0;transform:translate(-50%,110%)}'
            + '.dcms-action-dock.dcms-dock-top.is-visible,'
            + '.dcms-action-dock.dcms-dock-bottom.is-visible{transform:translate(-50%,0)}'
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-group,'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-group{flex-direction:row;align-items:center}'
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-group + .dcms-dock-group:not(:empty),'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-group + .dcms-dock-group:not(:empty){'
            + 'border-top:0;padding-top:0;border-left:1px solid var(--color-gray-200);padding-left:5px}'
            + 'html.dark .dcms-action-dock.dcms-dock-top .dcms-dock-group + .dcms-dock-group:not(:empty),'
            + 'html.dark .dcms-action-dock.dcms-dock-bottom .dcms-dock-group + .dcms-dock-group:not(:empty){'
            + 'border-left-color:var(--color-neutral-600)}'
            // The hide/move/collapse controls sit inline in the row — the
            // negative vertical margins tuned for the column layout would skew
            // them.
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-hide,'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-hide,'
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-move,'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-move{margin:0 1px 0 -2px}'
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-collapse,'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-collapse{margin:0 1px 0 -2px;padding:0 1px}'
            + '.dcms-action-dock.dcms-dock-top .dcms-dock-flyout{'
            + 'left:0;right:auto;top:calc(100% + 12px);bottom:auto}'
            + '.dcms-action-dock.dcms-dock-bottom .dcms-dock-flyout{'
            + 'left:0;right:auto;bottom:calc(100% + 12px);top:auto}'
            + '.dcms-dock-dot.dcms-dock-top,.dcms-dock-dot.dcms-dock-bottom{'
            + 'left:50%;right:auto;transform:translateX(-50%);width:26px;height:22px}'
            + '.dcms-dock-dot.dcms-dock-top{top:0;bottom:auto}'
            + '.dcms-dock-dot.dcms-dock-bottom{bottom:0;top:auto}'
            + '@media (max-width:640px){'
            + '.dcms-action-dock{bottom:76px}'
            + '.dcms-dock-dot{bottom:76px}'
            + '.dcms-dock-collapse{display:flex}'
            + '.dcms-action-dock.is-collapsed{gap:0;padding:3px 2px}'
            + '.dcms-action-dock.is-collapsed>*:not(.dcms-dock-collapse){display:none!important}'
            + '.dcms-action-dock.is-collapsed .dcms-dock-collapse{margin:0}'
            // On narrow screens the flyout can no longer open to the right without
            // running off-screen — anchor it to the left edge and let it grow
            // upward with wrapping labels, clamped to the viewport width.
            + '.dcms-dock-flyout{left:0;right:auto;bottom:calc(100% + 8px);'
            + 'max-width:calc(100vw - 20px);white-space:normal}'
            + '.dcms-action-dock.dcms-dock-right .dcms-dock-flyout{left:auto;right:0}'
            + '.dcms-dock-flyout .btn{white-space:normal;text-align:left}'
            // Phone form mode: a full-width bar (centered with left:50% it
            // would only get half of the screen and wrap into two rows; no
            // overflow scrolling — it would clip the flyouts), and the bar
            // must not cover the end of the form.
            + 'html.dcms-dock-phone-form body{padding-bottom:64px}'
            + 'html.dcms-dock-phone-form .dcms-action-dock.dcms-dock-bottom{left:0;right:0;max-width:none;'
            + 'border-left:0;border-right:0;border-radius:0;transform:translateY(110%)}'
            + 'html.dcms-dock-phone-form .dcms-action-dock.dcms-dock-bottom.is-visible{transform:none}'
            // ...and its flyouts (save variants) open across the screen above
            // the bar instead of squeezing their labels into the width of
            // the trigger.
            + 'html.dcms-dock-phone-form .dcms-action-dock.dcms-dock-bottom .dcms-dock-flyout{'
            + 'position:fixed;left:8px;right:8px;bottom:60px;max-width:none}'
            + '}';
        var style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
    }

    function makeButton(icon, title, className, onClick, withLabel) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn size-3 ' + (withLabel ? '' : 'icon-only ') + (className || 'bg-border-gray');
        btn.title = title;
        btn.setAttribute('aria-label', title);
        btn.innerHTML = '<i class="msr">' + icon + '</i>' + (withLabel ? ' ' + escHtml(title) : '');
        if (onClick) { btn.addEventListener('click', onClick); }
        return btn;
    }

    // Plain borderless icon — the shared chrome of the system commands
    // (hide, move, scroll-to-top, open-menu), as opposed to the boxed
    // record/custom action buttons.
    function makeSystemButton(icon, title, onClick) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dcms-dock-sys';
        btn.title = title;
        btn.setAttribute('aria-label', title);
        btn.innerHTML = '<i class="msr">' + icon + '</i>';
        if (onClick) { btn.addEventListener('click', onClick); }
        return btn;
    }

    function escHtml(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
        });
    }

    /**
     * Trigger button + submenu. items: [{icon,label,colorClass,onClick}].
     * `render` (optional) turns the submenu into a page-owned panel: it is
     * emptied and handed to the callback on every open, so its content is
     * always freshly built (see flyoutRender in the API docs).
     * Returns {el, trigger, itemButtons, panel}.
     */
    function makeFlyout(triggerIcon, triggerTitle, triggerClass, items, render) {
        var wrap = document.createElement('div');
        wrap.className = 'dcms-dock-item';

        var panel = document.createElement('div');
        panel.className = 'dcms-dock-flyout' + (render ? ' is-panel' : '');

        var itemButtons = (items || []).map(function (it) {
            var b = makeButton(it.icon, it.label, it.colorClass || 'bg-border-gray', function () {
                closeFlyouts();
                it.onClick();
            }, true);
            panel.appendChild(b);
            return b;
        });

        var trigger = makeButton(triggerIcon, triggerTitle, triggerClass, function (e) {
            e.stopPropagation();
            var open = wrap.classList.contains('is-open');
            closeFlyouts();
            if (open) { return; }
            wrap.classList.add('is-open');
            if (!render) { return; }
            panel.innerHTML = '';
            // A page callback must never leave the dock with a half-open,
            // empty panel — report the failure in place and keep going.
            try {
                render(panel);
            } catch (err) {
                panel.textContent = t('flyoutFailed', 'Nie udało się otworzyć sekcji.');
            }
            clampFlyout(panel);
        });

        wrap.appendChild(trigger);
        wrap.appendChild(panel);
        return { el: wrap, trigger: trigger, itemButtons: itemButtons, panel: panel };
    }

    // A page-owned panel is far wider than a submenu of buttons, so the edge
    // anchoring (left:0 on the horizontal variants, left:100% on the vertical
    // ones) can push it past the viewport. Nudge it back horizontally after
    // it renders; the CSS max-width caps the other axis.
    function clampFlyout(panel) {
        panel.style.transform = '';
        var r = panel.getBoundingClientRect();
        var margin = 8;
        var shift = 0;
        if (r.right > window.innerWidth - margin) {
            shift = (window.innerWidth - margin) - r.right;
        }
        if (r.left + shift < margin) {
            shift = margin - r.left;
        }
        if (shift) { panel.style.transform = 'translateX(' + Math.round(shift) + 'px)'; }
    }

    function closeFlyouts() {
        if (!dock) { return; }
        [].forEach.call(dock.querySelectorAll('.dcms-dock-item.is-open'), function (el) {
            el.classList.remove('is-open');
        });
    }

    function bindFlyoutClose() {
        document.addEventListener('click', function (e) {
            if (!dock.contains(e.target)) { closeFlyouts(); }
        });
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') { closeFlyouts(); }
        });
    }

    function build() {
        dock = document.createElement('div');
        dock.id = 'dcmsActionDock';
        dock.className = 'dcms-action-dock';

        collapseBtn = document.createElement('button');
        collapseBtn.type = 'button';
        collapseBtn.className = 'dcms-dock-collapse';
        collapseBtn.addEventListener('click', toggleCollapsed);

        hideBtn = makeSystemButton('close', t('hide', 'Ukryj pasek akcji'), function () {
            applyHidden(true);
        });
        hideBtn.classList.add('dcms-dock-hide');

        moveBtn = makeSystemButton('open_with', '', cycleEdge);
        moveBtn.classList.add('dcms-dock-move');

        navGroup = document.createElement('div');
        navGroup.className = 'dcms-dock-group';
        navGroup.appendChild(makeSystemButton('vertical_align_top', t('top', 'Do góry'), function () {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }));
        var menuButton = document.getElementById('menu-button');
        if (menuButton) {
            navGroup.appendChild(makeSystemButton('menu', t('menu', 'Otwórz menu'), function () {
                menuButton.click();
            }));
        }

        recordGroup = document.createElement('div');
        recordGroup.className = 'dcms-dock-group';

        customWrap = document.createElement('div');
        customWrap.style.display = 'contents';

        metaGroup = document.createElement('div');
        metaGroup.className = 'dcms-dock-group';

        dock.appendChild(hideBtn);
        dock.appendChild(moveBtn);
        dock.appendChild(collapseBtn);
        dock.appendChild(navGroup);
        dock.appendChild(recordGroup);
        dock.appendChild(customWrap);
        dock.appendChild(metaGroup);
        document.body.appendChild(dock);

        // Restore affordances — separate fixed elements (the dock is display-
        // hidden while retracted, so a dot can't live inside it). One per edge:
        // the active edge's dot restores a hidden dock; in the 'both' position
        // mode the opposite edge's dot moves the dock over to that edge. The
        // top/bottom dots only ever show as restore targets (no 'both' mode on
        // the horizontal axis).
        dotLeft = makeDot('left');
        dotRight = makeDot('right');
        dotTop = makeDot('top');
        dotBottom = makeDot('bottom');
    }

    function makeDot(dotSide) {
        var el = document.createElement('button');
        el.type = 'button';
        el.className = 'dcms-dock-dot' + (dotSide === 'left' ? '' : ' dcms-dock-' + dotSide);
        // Chevron pointing into the screen, the way the dock slides back in.
        // Rendered up front and revealed by CSS in the restore state only (see
        // the .is-restore rules) — the 'both' mode's switch target keeps the dot.
        el.innerHTML = '<i class="msr">' + (DOT_CHEVRONS[dotSide] || DOT_CHEVRONS.left) + '</i>';
        el.title = t('show', 'Pokaż pasek akcji');
        el.setAttribute('aria-label', el.title);
        el.addEventListener('click', function () { onDotClick(dotSide); });
        document.body.appendChild(el);
        return el;
    }

    function onDotClick(dotSide) {
        if (posPref === 'both' && dotSide !== side) { setSide(dotSide, true); }
        // A dot can be on screen before any scroll (dockDot 'always') — a click
        // then means "show me the dock NOW", so force the reveal until the page
        // scrolls back above the threshold.
        if (!visible) { setRevealOverride(true); }
        applyHidden(false);
    }

    function setSide(next, persist) {
        side = (next === 'right' || next === 'top' || next === 'bottom') ? next : 'left';
        if (persist && posPref === 'both') {
            try { localStorage.setItem(SIDE_KEY, side); } catch (e) { /* noop */ }
        }
        closeFlyouts();
        renderCollapse();
        renderMove();
        updateTopOffset();
        applyVisibility();
    }

    /* ---- move control (cycle through the edges) --------------------------- */

    // The stored manual edge, or null when the user never picked one (or the
    // stored value is garbage).
    function readEdgeOverride() {
        var v = null;
        try { v = localStorage.getItem(EDGE_KEY); } catch (e) { /* noop */ }
        return (EDGE_ORDER.indexOf(v) !== -1) ? v : null;
    }

    function nextEdge() {
        return EDGE_ORDER[(EDGE_ORDER.indexOf(side) + 1) % EDGE_ORDER.length];
    }

    function cycleEdge() {
        var next = nextEdge();
        try { localStorage.setItem(EDGE_KEY, next); } catch (e) { /* noop */ }
        // A manual pick pins the dock to that single edge — the 'both' switch
        // dots stand down until the preferences form saves again (which clears
        // the override).
        posPref = next;
        setSide(next, false);
    }

    // The tooltip names the edge the NEXT click moves the dock to.
    function renderMove() {
        if (!moveBtn) { return; }
        var names = {
            left: t('edgeLeft', 'lewa strona'),
            top: t('edgeTop', 'góra'),
            right: t('edgeRight', 'prawa strona'),
            bottom: t('edgeBottom', 'dół')
        };
        var title = t('move', 'Przenieś pasek akcji') + ' → ' + names[nextEdge()];
        if (moveBtn.title !== title) {
            moveBtn.title = title;
            moveBtn.setAttribute('aria-label', title);
        }
    }

    // The topbar (z-index above the dock) would cover a bar pinned to top:0
    // while the page is not scrolled — exactly the state setAlwaysVisible
    // pages boot in. Instead of stacking games the top variant rides BELOW
    // the topbar's on-screen remainder and docks to the viewport edge once
    // the header has scrolled away; the restore dot follows the same line.
    function updateTopOffset() {
        var isTop = (side === 'top');
        var off = '';
        if (isTop) {
            var tb = document.querySelector('header.topbar');
            var bottom = tb ? tb.getBoundingClientRect().bottom : 0;
            off = Math.max(0, bottom) + 'px';
        }
        if (dock) { dock.style.top = off; }
        if (dotTop) { dotTop.style.top = off; }
    }

    /* ---- public API ------------------------------------------------------ */

    function exposeApi() {
        window.DCMSActionDock = {
            register: function (groupId, buttons) {
                if (!groupId || !Array.isArray(buttons) || !buttons.length) { return; }
                // Adopt the current dataset document: registration usually happens
                // right after an iframe navigation, before the sync tick notices the
                // document swap — without this the very next tick would wipe the
                // freshly registered group as belonging to the previous document.
                trackedDoc = getDatasetDocument();
                this.unregister(groupId);
                var group = document.createElement('div');
                group.className = 'dcms-dock-group';
                group.dataset.dockGroup = groupId;
                buttons.forEach(function (cfg) {
                    var render = (typeof cfg.flyoutRender === 'function') ? cfg.flyoutRender : null;
                    if (render || (cfg.flyout && cfg.flyout.length)) {
                        group.appendChild(makeFlyout(
                            cfg.icon,
                            cfg.label,
                            cfg.colorClass || 'bg-border-gray',
                            cfg.flyout || [],
                            render
                        ).el);
                    } else {
                        group.appendChild(makeButton(cfg.icon, cfg.label, cfg.colorClass || 'bg-border-gray', cfg.onClick));
                    }
                });
                customGroups[groupId] = group;
                customWrap.appendChild(group);
            },
            unregister: function (groupId) {
                if (customGroups[groupId]) {
                    customGroups[groupId].remove();
                    delete customGroups[groupId];
                }
            },
            // Opt-in for pages that want the dock revealed from load (no scroll
            // threshold). Cleared automatically on dataset iframe navigation.
            setAlwaysVisible: setAlwaysVisible,
            // The dock's background save, callable from the framed page — so a form
            // can offer its own "save and stay" control running the exact same
            // pipeline (gesture replay → hijacked submit → fetch → resync → events).
            // No-op when the page has no save button. Takes no arguments: the
            // options object is internal (retry bookkeeping).
            saveAjax: function () { ajaxSave(); },
            // Feature detection for page code that must stay compatible with an
            // older dock bundle (registration silently ignores unknown keys).
            supports: { flyoutRender: true, saveAjax: true }
        };
    }

    function clearCustomGroups() {
        Object.keys(customGroups).forEach(function (id) { window.DCMSActionDock.unregister(id); });
    }

    /* ---- appearance preferences (position / restore dot) ------------------ */

    // Injected by the host page (DCMS::render, Master mode) from the user's
    // profile (user_settings → dcms2.interface); {} on pages without it.
    function readInterfaceSettings() {
        var s = window.__DCMS_INTERFACE_SETTINGS__;
        return (s && typeof s === 'object') ? s : {};
    }

    function applyInterfacePrefs(settings) {
        lastPrefs = settings || {};
        var pos = settings.dockPosition;
        posPref = (pos === 'right' || pos === 'both' || pos === 'top' || pos === 'bottom') ? pos : 'left';
        dotAlways = settings.dockDot === 'always';
        // A manual pick made with the move control wins over the stored
        // preference (localStorage > database, as the settings form documents)
        // and pins the dock to that single edge.
        var override = readEdgeOverride();
        if (override) {
            posPref = override;
            setSide(override, false);
        } else if (posPref === 'both') {
            var stored = null;
            try { stored = localStorage.getItem(SIDE_KEY); } catch (e) { /* noop */ }
            setSide(stored === 'right' ? 'right' : 'left', false);
        } else {
            setSide(posPref, false);
        }
        if (phoneForm) { applyPhoneSide(); }
    }

    // Live re-apply when the appearance-preferences form (in the dataset
    // iframe) saves: it posts 'dcms-apply-settings' with the fresh settings to
    // the host window (the same message main.js uses for theme/layout).
    function bindSettingsMessages() {
        window.addEventListener('message', function (event) {
            if (event.origin !== window.location.origin) { return; }
            var data = event.data;
            if (!data || typeof data !== 'object' || data.type !== 'dcms-apply-settings') { return; }
            if (!data.settings || typeof data.settings !== 'object') { return; }
            // An explicit preferences save supersedes any quick reposition made
            // with the move control.
            try { localStorage.removeItem(EDGE_KEY); } catch (e) { /* noop */ }
            applyInterfacePrefs(data.settings);
        });
    }

    /* ---- collapse (narrow screens) --------------------------------------- */

    // The collapse state only matters under the narrow breakpoint (the CSS
    // that hides the groups is media-gated); on wide screens the class is inert
    // and the handle is hidden, so the dock always shows in full there.
    function readStoredCollapsed() {
        try { return localStorage.getItem(COLLAPSE_KEY); } catch (e) { return null; }
    }

    function renderCollapse() {
        if (!collapseBtn) { return; }
        var title = collapsed ? t('expand', 'Rozwiń pasek akcji') : t('collapse', 'Zwiń pasek akcji');
        // The chevron points towards the edge the dock retracts to, so its
        // direction follows the active edge (horizontal edges use the
        // vertical expand icons).
        var CHEVRONS = {
            left: ['chevron_left', 'chevron_right'],
            right: ['chevron_right', 'chevron_left'],
            top: ['expand_less', 'expand_more'],
            bottom: ['expand_more', 'expand_less']
        };
        var pair = CHEVRONS[side] || CHEVRONS.left;
        var towardEdge = pair[0], intoScreen = pair[1];
        collapseBtn.innerHTML = '<i class="msr">' + (collapsed ? intoScreen : towardEdge) + '</i>';
        collapseBtn.title = title;
        collapseBtn.setAttribute('aria-label', title);
    }

    function applyCollapsed(next, persist) {
        collapsed = !!next;
        if (dock) { dock.classList.toggle('is-collapsed', collapsed); }
        if (collapsed) { closeFlyouts(); }
        renderCollapse();
        if (persist) {
            try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch (e) { /* noop */ }
        }
    }

    function toggleCollapsed() { applyCollapsed(!collapsed, true); }

    /* ---- hide / restore (any width) -------------------------------------- */

    function applyHidden(next) {
        hidden = !!next;
        applyVisibility();
    }

    // The dock always boots visible: hiding lives in `hidden` alone, for the
    // lifetime of this page. Builds up to 1.10.97 wrote the choice to
    // localStorage, which is exactly how editors ended up bar-less across
    // sessions — drop that leftover instead of merely ignoring it.
    function initHidden() {
        try { localStorage.removeItem(HIDE_KEY); } catch (e) { /* noop */ }
        applyHidden(false);
    }

    function initCollapsed() {
        // Explicit user choice wins; otherwise default to collapsed on narrow
        // viewports (where the full dock would cover the single content column)
        // and expanded on wide ones.
        var stored = readStoredCollapsed();
        if (stored === '1' || stored === '0') {
            applyCollapsed(stored === '1', false);
        } else {
            applyCollapsed(narrowMq ? narrowMq.matches : false, false);
        }
        if (narrowMq) {
            var onChange = function () {
                // Crossing the breakpoint re-applies the default only while the
                // user hasn't pinned a choice — an explicit tap keeps sticking.
                updatePhoneForm();
                if (readStoredCollapsed() !== null) { return; }
                applyCollapsed(narrowMq.matches && !phoneForm, false);
            };
            if (narrowMq.addEventListener) { narrowMq.addEventListener('change', onChange); }
            else if (narrowMq.addListener) { narrowMq.addListener(onChange); }
        }
    }

    /* ---- phone form mode (narrow screens) -------------------------------- */

    function datasetFormPresent(doc) {
        try { return !!(doc && doc.querySelector('form[name="datasetEditForm"]')); } catch (e) { return false; }
    }

    // The bottom bar, unless the user picked an edge with the move control or
    // prefers a horizontal bar anyway (dockPosition 'top' keeps the top one).
    function applyPhoneSide() {
        if (readEdgeOverride() || side === 'top' || side === 'bottom') { return; }
        setSide('bottom', false);
    }

    function updatePhoneForm() {
        var next = !!(narrowMq && narrowMq.matches) && datasetFormPresent(getDatasetDocument());
        if (next === phoneForm) { return; }
        phoneForm = next;
        document.documentElement.classList.toggle('dcms-dock-phone-form', phoneForm);
        if (phoneForm) {
            applyPhoneSide();
            if (readStoredCollapsed() !== '1') { applyCollapsed(false, false); }
        } else {
            applyInterfacePrefs(lastPrefs);
            if (readStoredCollapsed() === null) { applyCollapsed(!!(narrowMq && narrowMq.matches), false); }
        }
        updateSync();
        applyVisibility();
    }

    // The dataset iframe navigates on its own (list → form → list), so the
    // mode is re-checked on a light timer — only while the window is narrow.
    function initPhoneForm() {
        if (!narrowMq) { return; }
        var watch = function () {
            if (narrowMq.matches && !phoneFormTimer) {
                phoneFormTimer = window.setInterval(updatePhoneForm, SYNC_INTERVAL_MS);
            } else if (!narrowMq.matches && phoneFormTimer) {
                window.clearInterval(phoneFormTimer);
                phoneFormTimer = null;
            }
            updatePhoneForm();
        };
        if (narrowMq.addEventListener) { narrowMq.addEventListener('change', watch); }
        else if (narrowMq.addListener) { narrowMq.addListener(watch); }
        watch();
    }

    /* ---- visibility ------------------------------------------------------ */

    function bindScroll() {
        var ticking = false;
        window.addEventListener('scroll', function () {
            if (ticking) { return; }
            ticking = true;
            window.requestAnimationFrame(function () {
                ticking = false;
                var past = window.scrollY > SHOW_AFTER_PX;
                // Returning above the threshold ends a dot-click forced reveal.
                if (!past) { setRevealOverride(false); }
                updateTopOffset();
                setVisible(past);
            });
        }, { passive: true });
        window.addEventListener('resize', updateTopOffset);
        updateTopOffset();
        setVisible(window.scrollY > SHOW_AFTER_PX);
    }

    // The dock counts as revealed by scroll, by a dot-click override, or by a
    // page-requested always-on mode.
    function effectiveVisible() { return visible || revealOverride || alwaysVisible || phoneForm; }

    function setAlwaysVisible(next) {
        next = !!next;
        if (next === alwaysVisible) { return; }
        // Adopt the current dataset document when enabling — the request comes
        // from a freshly navigated iframe page, and the very next sync tick
        // would otherwise treat that document as "new" and clear the mode
        // right back (same adoption pattern as register()).
        if (next) { trackedDoc = getDatasetDocument(); }
        alwaysVisible = next;
        updateSync();
        applyVisibility();
    }

    // Record-action mirroring must run whenever the dock is on screen — no
    // matter whether scroll or a dot click revealed it.
    function updateSync() {
        if (effectiveVisible()) {
            syncRecordActions();
            syncFeedbackAction();
            if (!syncTimer) { syncTimer = window.setInterval(syncRecordActions, SYNC_INTERVAL_MS); }
        } else if (syncTimer) {
            window.clearInterval(syncTimer);
            syncTimer = null;
        }
    }

    function setVisible(next) {
        if (next === visible) { return; }
        visible = next;
        updateSync();
        applyVisibility();
    }

    function setRevealOverride(next) {
        if (next === revealOverride) { return; }
        revealOverride = next;
        updateSync();
        applyVisibility();
    }

    // Splits the reveal state by the user's hide choice and position prefs:
    // revealed & not hidden → the dock rides in on its active edge (in 'both'
    // mode the opposite edge shows the switch dot); revealed & hidden → the
    // restore dot appears on the active edge ('both': on both edges); not
    // revealed → nothing, unless dockDot 'always' keeps the dots on screen.
    function applyVisibility() {
        var revealed = effectiveVisible();
        var showDock = revealed && !hidden;
        if (dock) {
            dock.classList.toggle('dcms-dock-right', side === 'right');
            dock.classList.toggle('dcms-dock-top', side === 'top');
            dock.classList.toggle('dcms-dock-bottom', side === 'bottom');
            dock.classList.toggle('is-visible', showDock);
        }
        syncDot(dotLeft, 'left', revealed);
        syncDot(dotRight, 'right', revealed);
        syncDot(dotTop, 'top', revealed);
        syncDot(dotBottom, 'bottom', revealed);
        if (!showDock) { closeFlyouts(); }
    }

    function syncDot(el, dotSide, revealed) {
        if (!el) { return; }
        // The 'both' mode spans the vertical edges only — its extra dots must
        // never leak onto the horizontal ones.
        var vertical = (dotSide === 'left' || dotSide === 'right');
        var wanted = hidden
            ? (dotSide === side || (posPref === 'both' && vertical))
            : (posPref === 'both' && vertical && dotSide !== side);
        var show = wanted && (dotAlways || revealed);
        el.classList.toggle('is-visible', show);
        // Two roles, two looks: restoring a hidden dock gets the loud handle,
        // moving a visible dock between edges keeps the quiet dot.
        el.classList.toggle('is-restore', hidden);
        var title = (!hidden && posPref === 'both' && dotSide !== side)
            ? t('moveDock', 'Przenieś pasek akcji na tę stronę')
            : t('show', 'Pokaż pasek akcji');
        if (el.title !== title) {
            el.title = title;
            el.setAttribute('aria-label', title);
        }
    }

    /* ---- record actions (save flyout) ------------------------------------ */

    function getDatasetDocument() {
        var frame = document.querySelector('iframe[id^="dcmsiframe"], #dcmsContentIframe, iframe.dcms-iframe');
        if (!frame) { return null; }
        try { return frame.contentDocument; } catch (e) { return null; }
    }

    // Dataset mode of the framed page ('edit' | 'insert' | 'view' | …), read from its
    // URL — the save pipeline behaves differently per mode (see ajaxSave).
    function datasetMode(doc) {
        try {
            return new URL(doc.defaultView.location.href).searchParams.get('datasetMode') || '';
        } catch (e) {
            return '';
        }
    }

    function findRealSubmitButtons(doc) {
        if (!doc) { return []; }
        var list = doc.querySelectorAll('form[name="datasetEditForm"] button[type="submit"][name="submitAction"]');
        return [].slice.call(list).filter(function (b) { return b.offsetParent !== null; });
    }

    /**
     * The edit/insert/view form's "return without saving" control. Framework
     * handlers render it as an <a>/<button> carrying the arrow_back icon and
     * the bg-light-neutral class (client-specific back links use other colors),
     * so that pair identifies it without coupling to the localized label. An
     * explicit [data-dcms-return] hook, if a page adds one, wins. Page
     * navigation shows two copies (header + bottom) — either drives the same
     * return, so the first visible one is returned.
     */
    function findReturnButton(doc) {
        if (!doc) { return null; }
        var hook = doc.querySelector('[data-dcms-return]');
        if (hook && hook.offsetParent !== null) { return hook; }
        var list = doc.querySelectorAll('a.bg-light-neutral, button.bg-light-neutral');
        for (var i = 0; i < list.length; i++) {
            var b = list[i];
            if (b.offsetParent === null) { continue; }
            var icon = b.querySelector('.msr, .material-symbols-rounded');
            if (icon && icon.textContent.trim() === 'arrow_back') { return b; }
        }
        return null;
    }

    function buttonLabel(btn) {
        var clone = btn.cloneNode(true);
        var icons = clone.querySelectorAll('.msr, .material-symbols-rounded');
        [].forEach.call(icons, function (i) { i.parentNode.removeChild(i); });
        return clone.textContent.replace(/\s+/g, ' ').trim();
    }

    function buttonIcon(btn) {
        var icon = btn.querySelector('.msr, .material-symbols-rounded');
        return (icon && icon.textContent.trim()) || 'save';
    }

    function buttonColorClass(btn) {
        var match = btn.className.match(/\bbg-(?!border-)[a-z-]+\b/);
        return match ? match[0] : 'bg-green';
    }

    function isInactive(btn) {
        if (btn.disabled) { return true; }
        var win = btn.ownerDocument.defaultView;
        return parseFloat(win.getComputedStyle(btn).opacity) < 0.9;
    }

    /**
     * Replays the user gesture the edit form expects: pointerdown (primes
     * editor pre-flush / progress UI) followed by a click after a human-like
     * gap, so the native submit fast path applies.
     */
    function proxySubmit(realBtn) {
        var win = realBtn.ownerDocument.defaultView;
        try { win.focus(); } catch (e) { /* noop */ }
        var Ev = win.PointerEvent || win.MouseEvent;
        realBtn.dispatchEvent(new Ev('pointerdown', { bubbles: true }));
        window.setTimeout(function () { realBtn.click(); }, PROXY_CLICK_DELAY_MS);
    }

    /**
     * Fires the real return button. A plain click is enough: the form's own
     * capture-phase guard (unsaved-changes confirm) and the button's inline
     * navigation / modal-close all hang off that click, so the flow matches a
     * direct press — no submit fast path, hence no pointerdown priming.
     */
    function proxyReturn(realBtn) {
        var win = realBtn.ownerDocument.defaultView;
        try { win.focus(); } catch (e) { /* noop */ }
        realBtn.click();
    }

    function syncRecordActions() {
        var doc = getDatasetDocument();
        if (doc !== trackedDoc) {
            // Adopting the first document of this page load is not a
            // navigation: syncing only runs once the dock is on screen, so the
            // very first tick can land AFTER the user has already hidden the
            // dock — treating that as a move would undo the hide on the spot.
            var moved = (trackedDoc !== null);
            trackedDoc = doc;
            clearCustomGroups();   // registrations belong to the previous document
            setAlwaysVisible(false);   // same lifecycle: requested by that document
            // Hiding was scoped to the document the user hid it on: moving to
            // another record (or back to the listing) brings the dock back, so
            // nobody carries a stray × from one screen into every next one.
            if (moved && hidden) { applyHidden(false); }
        }

        var real = findRealSubmitButtons(doc);
        var hasReturn = !!findReturnButton(doc);
        var signature = (hasReturn ? 'R;' : '') +
            real.map(function (b) { return buttonLabel(b) + '|' + buttonColorClass(b); }).join(';');

        if (signature !== recordSignature) {
            recordSignature = signature;
            recordGroup.innerHTML = '';
            recordFlyout = null;

            // "Return without saving" — first in the group so arrow_back reads as
            // leaving the form. Proxies the real return button (findReturnButton)
            // so the page's own unsaved-changes confirm, inline navigation and
            // modal-close all run exactly as on a direct press. Present in view
            // mode too, where there is no save button.
            if (hasReturn) {
                var returnBtn = findReturnButton(doc);
                var returnLabel = (returnBtn && buttonLabel(returnBtn)) ||
                    t('returnWithoutSaving', 'Powrót bez zapisywania zmian');
                recordGroup.appendChild(makeButton('arrow_back', returnLabel, 'bg-border-gray', function () {
                    var fresh = findReturnButton(getDatasetDocument());
                    if (fresh) { proxyReturn(fresh); }
                }));
            }

            if (real.length) {
                // The AJAX save is the DEFAULT save: it keeps the user on the form and
                // costs no reload, so it carries the plain "Zapisz" label; the mirrors
                // below keep whatever the real form buttons are called ("Zapisz dane…").
                var items = [{
                    icon: 'bolt',
                    label: t('saveAjax', 'Zapisz'),
                    colorClass: 'bg-teal',
                    onClick: ajaxSave
                }];
                real.forEach(function (rb, i) {
                    items.push({
                        icon: buttonIcon(rb),
                        label: buttonLabel(rb),
                        colorClass: buttonColorClass(rb),
                        onClick: function () {
                            var fresh = findRealSubmitButtons(getDatasetDocument());
                            if (fresh[i]) { proxySubmit(fresh[i]); }
                        }
                    });
                });
                recordFlyout = makeFlyout('save', t('save', 'Zapisz'), 'bg-green', items);
                recordGroup.appendChild(recordFlyout.el);
            }
        }

        if (recordFlyout) {
            var allInactive = real.length > 0 && real.every(isInactive);
            recordFlyout.trigger.classList.toggle('dcms-dock-inactive', allInactive);
            recordFlyout.itemButtons.forEach(function (b, idx) {
                // item 0 = AJAX save (state follows the whole set), 1..n = mirrors
                var inactive = (idx === 0) ? allInactive : (real[idx - 1] ? isInactive(real[idx - 1]) : false);
                b.classList.toggle('dcms-dock-inactive', inactive);
            });
        }
    }

    /* ---- AJAX save (no page reload) --------------------------------------- */

    function toast(ok, message) {
        if (window.iziToast) {
            window.iziToast[ok ? 'success' : 'error']({
                title: ok ? 'OK' : 'Błąd',
                message: message,
                position: 'bottomLeft',
                timeout: ok ? 3500 : 6500
            });
        } else if (!ok) {
            console.error('[ActionDock] ' + message);
        }
    }

    // A gate may stop the save without ever submitting (editor validation,
    // media-meta) — after this window the hijack disarms so a later,
    // user-initiated native save is not converted into an AJAX one.
    var AJAX_HIJACK_DISARM_MS = 12000;

    // Resolves false only when the session guard (dcms-session-guard.js, top window)
    // confirms an ended session — it shows its sign-in overlay at the same time. No
    // guard, a network error or an unknown answer resolve true: the caller goes on as
    // it always did.
    function sessionStillValid() {
        var guard = window.DCMSSessionGuard;
        if (!guard || typeof guard.checkState !== 'function') { return Promise.resolve(true); }
        return guard.checkState('save').then(function (logged) { return logged !== false; },
            function () { return true; });
    }

    // Framework-owned hidden inputs the server re-issues on every render and
    // whose value the save itself invalidates. A full save navigates, so the
    // browser picks the new values up for free; an AJAX save does not, and the
    // stale copy would be posted again by the NEXT save.
    //
    // datasetConflictBase: optimistic-locking baseline. The save has just
    // bumped the record's modification timestamp, so keeping the pre-save
    // baseline makes the next save report a conflict against our own write.
    //
    // Deliberately an allow-list, never "every hidden input": the point of
    // saving without a reload is that the user keeps editing, so page-owned
    // state (staged widget payloads, editor buffers) must stay untouched.
    var RESYNC_HIDDEN = ['datasetConflictBase'];

    // Plain-text reason the server attaches to a withheld save (write conflict).
    // Absent on validation re-renders and on backends predating the field, so
    // its absence simply means "no conflict information" — never an error.
    var CONFLICT_NOTICE = 'datasetConflictNotice';

    /** Parses a save response into a document, or null when unreadable. */
    function parseResponse(form, html) {
        if (!html) { return null; }
        try {
            return new form.ownerDocument.defaultView.DOMParser()
                .parseFromString(html, 'text/html');
        } catch (e) {
            return null;
        }
    }

    /** The server's conflict explanation from a parsed response, or null. */
    function readConflictNotice(parsed) {
        try {
            var el = parsed && parsed.querySelector('input[name="' + CONFLICT_NOTICE + '"]');
            var text = el ? String(el.value || '').trim() : '';
            return text !== '' ? text : null;
        } catch (e) {
            return null;
        }
    }

    /**
     * Copies RESYNC_HIDDEN values from a parsed response into the live form.
     * Returns { document, resynced } for the event detail. Every step is
     * optional — a form without these fields, or a response without them, is
     * left exactly as it was.
     */
    function applyServerState(form, parsed) {
        var resynced = {};
        RESYNC_HIDDEN.forEach(function (name) {
            try {
                var fresh = parsed.querySelector('input[type="hidden"][name="' + name + '"]');
                var live = form.elements[name];
                // form.elements returns a RadioNodeList for repeated names —
                // only a single input has a plain string value to overwrite.
                if (!fresh || !live || typeof live.value !== 'string') { return; }
                if (live.value === fresh.value) { return; }
                live.value = fresh.value;
                resynced[name] = fresh.value;
            } catch (e) { /* one bad field must not stop the rest */ }
        });
        return { document: parsed, resynced: resynced };
    }

    /** Parse + apply in one step — the successful-save path. */
    function resyncServerState(form, html) {
        var parsed = parseResponse(form, html);
        return parsed ? applyServerState(form, parsed) : null;
    }

    // opts.retrying marks the one replay that follows a confirmed overwrite.
    // Read off an object, never a positional flag: this function is also used
    // directly as a click handler, so a DOM event may arrive as the argument.
    function ajaxSave(opts) {
        var retrying = !!(opts && opts.retrying === true);
        var doc = getDatasetDocument();
        var real = findRealSubmitButtons(doc);
        if (!real.length) { return; }
        var btn = real[0];
        if (real.every(isInactive)) { return; }
        var form = btn.form;
        var win = doc.defaultView;

        var isEdit = datasetMode(doc) === 'edit';

        // Background save is EDIT-only. In INSERT the fetch would create the
        // record while the visible form STAYS in INSERT mode, so every further
        // save would insert another record — one duplicate per click. There is
        // also nothing to keep the user on: the only correct outcome of an
        // INSERT save is leaving the blank form (saveAndContinue answers with
        // a PRG into EDIT of the new record). Drive the real submit with full
        // navigation instead — same gates, same server flow, visible mode
        // switch included.
        if (!isEdit) {
            proxySubmit(btn);
            return;
        }

        if (recordFlyout) { recordFlyout.trigger.classList.add('dcms-dock-busy'); }
        var settle = function () {
            if (recordFlyout) { recordFlyout.trigger.classList.remove('dcms-dock-busy'); }
        };
        // Nothing was saved. The detail is additive — listeners that only watch
        // for the event itself keep working untouched.
        var rejected = function (message, parsed) {
            try {
                doc.dispatchEvent(new win.CustomEvent('dcms:dock:save-rejected', {
                    bubbles: true,
                    detail: { document: parsed || null, message: message }
                }));
            } catch (e) { /* a listener threw — not our failure */ }
            toast(false, message);
        };
        // The background save did not go through — replay it as a NORMAL submit so
        // the user ends up on the re-rendered page, where the server states the
        // reason (validation messages, error alert) instead of a bare toast. EDIT
        // only: there a save is an update, so repeating it is harmless, while in
        // INSERT the same replay could create a second record. Never used for the
        // write-conflict branch, where NOT saving is the user's own decision.
        //
        // Not replayed either when the save was refused because the session has
        // ended (signed out in another tab, absolute limit): the full submit would
        // post the edits into the sign-in page and they would be gone. The session
        // guard shows its sign-in overlay over the untouched form instead.
        var fallbackToFullSave = function (message) {
            if (!isEdit) { rejected(message, null); return; }
            sessionStillValid().then(function (valid) {
                if (!valid) { rejected(message, null); return; }
                rejected(message + ' ' + t('saveFullFallback', 'Ponawiam pełnym zapisem…'), null);
                var fresh = findRealSubmitButtons(getDatasetDocument());
                if (fresh.length) { proxySubmit(fresh[0]); }
            });
        };

        // One-shot hijack of the FINAL submit. The gesture replay below drives
        // the page's own save pipeline; capture listeners on the form run in
        // registration order, so ours — registered last — fires after the
        // page's interceptors have all let the submit through. We only swap
        // the navigation for a fetch of the exact same payload.
        var disarmTimer = null;
        function disarm() {
            if (disarmTimer) { window.clearTimeout(disarmTimer); disarmTimer = null; }
            form.removeEventListener('submit', hijack, true);
        }
        function hijack(ev) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
            disarm();
            var fd = new win.FormData(form);
            var submitter = ev.submitter || btn;
            if (submitter.name) {
                // A background save is by definition "save and stay": in EDIT it always
                // posts submitAction=saveAndContinue, whatever button drove the replay.
                // That is the branch answering with a PRG back to the FORM — the only
                // response carrying the fresh server state this save must resync
                // (see RESYNC_HIDDEN). The primary "save" action would redirect to the
                // LIST instead, leaving the still-open form with a stale baseline and
                // turning the NEXT save into a false write conflict. The server honours
                // the value even on panels that hide the button (InterfaceOptions
                // saveAndStayButtonDisabled). INSERT is left alone — there
                // saveAndContinue switches the handler into EDIT of the new record.
                var value = (isEdit && submitter.name === 'submitAction')
                    ? 'saveAndContinue'
                    : (submitter.value || '');
                fd.append(submitter.name, value);
            }
            win.fetch(form.action, { method: 'POST', body: fd, credentials: 'same-origin' }).then(function (resp) {
                // A successful dataset save answers with a PRG redirect; a plain
                // 200 is the form re-rendered with validation errors.
                if (resp.ok && resp.redirected) {
                    // The followed redirect landed on the re-rendered EDIT form:
                    // read it, take the server-owned state back, and hand the
                    // rest to page code. An unreadable body only costs us the
                    // resync — the save itself already succeeded.
                    return resp.text().catch(function () { return ''; }).then(function (html) {
                        // The record IS saved by now, so nothing below may be
                        // allowed to report a failure — a broken resync costs
                        // us the refreshed state, never the success message.
                        var sync = null;
                        try { sync = resyncServerState(form, html); } catch (e) { /* keep going */ }
                        try {
                            doc.dispatchEvent(new win.CustomEvent('dcms:dock:saved', {
                                bubbles: true,
                                detail: {
                                    html: html || '',
                                    document: sync ? sync.document : null,
                                    resynced: sync ? sync.resynced : {}
                                }
                            }));
                        } catch (e) { /* a listener threw — not our failure */ }
                        toast(true, t('savedAjax', 'Zapisano — możesz kontynuować edycję.'));
                    });
                } else if (resp.ok) {
                    // Plain 200 = the form came back re-rendered and NOTHING was
                    // saved: validation errors, or the write-conflict page. That
                    // page is discarded here, so without the steps below a
                    // conflict stays invisible AND its bumped baseline — the very
                    // thing that turns a second save into a deliberate overwrite —
                    // never reaches the live form, leaving the save unrepeatable.
                    return resp.text().catch(function () { return ''; }).then(function (html) {
                        var parsed = parseResponse(form, html);
                        var notice = readConflictNotice(parsed);
                        // Overwriting someone else's work is the user's decision,
                        // so ask BEFORE adopting the bumped baseline: declining
                        // keeps the old one, so the next save asks again instead
                        // of silently overwriting. A retry never re-asks — a
                        // conflict raised again means yet another save landed
                        // meanwhile, which is reported rather than looped on.
                        if (notice && !retrying) {
                            var overwrite = false;
                            try {
                                overwrite = win.confirm(notice + '\n\n' + t('saveConflictPrompt',
                                    'OK — nadpisz tamte zmiany. Anuluj — zachowaj je.'));
                            } catch (e) { overwrite = false; }
                            if (overwrite) {
                                try { applyServerState(form, parsed); } catch (e) { /* replay anyway */ }
                                ajaxSave({ retrying: true });
                                return;
                            }
                            rejected(t('saveConflictKept',
                                'Zapis wstrzymany — zmiany drugiej osoby zostały zachowane.'), parsed);
                            return;
                        }
                        if (notice) { rejected(notice, parsed); return; }
                        fallbackToFullSave(t('saveAjaxRejected', 'Zapis w tle nie został przyjęty.'));
                    });
                } else {
                    fallbackToFullSave(t('saveAjaxRejected', 'Zapis w tle nie został przyjęty.'));
                }
            }).catch(function (err) {
                fallbackToFullSave((err && err.message) ? err.message
                    : t('saveAjaxFailed', 'Zapis w tle nie powiódł się.'));
            }).then(settle);
        }
        form.addEventListener('submit', hijack, true);
        disarmTimer = window.setTimeout(function () { disarm(); settle(); }, AJAX_HIJACK_DISARM_MS);

        proxySubmit(btn);
    }

    /* ---- meta (feedback gadget proxy) ------------------------------------ */

    // The dock replaces the floating launcher: the original button is kept in
    // the DOM (the proxy delegates its click to it) but is no longer shown.
    function syncFeedbackAction() {
        var launcher = document.getElementById('dcmsFbLauncher');
        var proxy = metaGroup.firstElementChild;
        if (launcher && !proxy) {
            metaGroup.appendChild(makeButton('feedback', buttonLabel(launcher) || t('feedback', 'Zgłoś uwagę'), 'bg-border-indigo', function () {
                launcher.click();
            }));
            launcher.style.display = 'none';
        } else if (!launcher && proxy) {
            metaGroup.innerHTML = '';
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
